if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.detailsElement = this.querySelector(".product__check-stock");
        this.storesContainer = this.querySelector(".stores");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

        this.initQuantityHandlers();
        this.initVariantHandlers();

        this.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }));
        this.updateMediaGrouping();

        // if (!this.detailsElement) return;
        // this.detailsElement.addEventListener("toggle", () => {
        //   if (this.detailsElement.open) {
        //     const selectedVariant = this.getSelectedVariant(this);
        //     this.checkStockForVariant(selectedVariant.id, selectedVariant.title);
        //   }
        // });
      }

      async checkStockForVariant(variantId, variantTitle = "") {
        // Ensure the stores container exists
        if (!this.storesContainer) {
          // console.error("Stores container not found in the custom element.");
          return;
        }

        // Add loading class to indicate the process is ongoing
        this.storesContainer.classList.add("loading");

        try {
          // Fetch stock data from the API using the provided variant ID
          const response = await fetch(`https://shopify.retailexpress.com.au/api/shopify_stores/beyond-skate-city/outlets?variant_ids=${variantId}`);
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();

          // Update the selected variant title if provided
          if (variantTitle) {
            const variantTitleElement = this.querySelector(".selected-variant");
            if (variantTitleElement) {
              variantTitleElement.textContent = variantTitle;
            }
          }

          // Iterate over the outlets returned by the API and update existing HTML
          data.outlets.forEach((outlet) => {
            const storeElement = this.storesContainer.querySelector(`[data-store="${outlet.name}"]`);

            if (!storeElement) {
              console.warn(`Store element for ${outlet.name} not found.`);
              return;
            }

            // Update store name (if needed, though this is typically static)
            const nameElement = storeElement.querySelector(".store-item__name");
            if (nameElement) {
              nameElement.textContent = outlet.name;
            }

            // Update address (combine address1 and address2)
            const addressElement = storeElement.querySelector(".store-item__address");
            if (addressElement) {
              const addressLine1 = outlet.contact?.address1 || "No address available";
              const addressLine2 = outlet.contact?.address2 || "";

              addressElement.innerHTML = addressLine1 + (addressLine2 ? `<br>${addressLine2}` : "");
            }

            // Update contact (phone number)
            const contactElement = storeElement.querySelector(".store-item__contact");
            if (contactElement) {
              contactElement.textContent = `Phone: ${outlet.contact?.phone || "No phone available"}`;
            }

            // Update stock status
            const stockElement = storeElement.querySelector(".store-item__stock");
            if (stockElement) {
              const stockQuantity = outlet.stock[variantId] || 0;

              if (stockQuantity > 1) {
                // stockElement.textContent = `${stockQuantity} Available`;
                stockElement.textContent = `In Stock`;
                stockElement.classList.add("in-stock");
                stockElement.classList.remove("low-stock", "out-of-stock");
              } else if (stockQuantity === 1) {
                stockElement.textContent = "1 Left";
                stockElement.classList.add("low-stock");
                stockElement.classList.remove("in-stock", "out-of-stock");
              } else {
                stockElement.textContent = "Out of Stock";
                stockElement.classList.add("out-of-stock");
                stockElement.classList.remove("in-stock", "low-stock");
              }
            }
          });
        } catch (error) {
          console.error("Error fetching stock data:", error);

          // Display an error message if the API call fails
          this.storesContainer.innerHTML = "<p>Error fetching stock data. Please try again later.</p>";
        } finally {
          // Remove the loading class once the process is complete
          this.storesContainer.classList.remove("loading");
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initVariantHandlers() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (eventData) => {
          const variantId = eventData.productVariantId;
          this.compareVariantInCart(variantId);
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => html.querySelectorAll(".scroll-trigger").forEach((element) => element.classList.add("scroll-trigger--cancel")));
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML = html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(document.querySelector("main"), html.querySelector("main"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          } else {
            HTMLUpdateUtility.viewTransition(this, html.querySelector("product-info"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, "text/html");
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getVariantInCart(productInfoNode) {
        const variantInCart = productInfoNode.querySelector("variant-selects [data-variant-in-cart]")?.innerHTML;
        const variantInCartData = JSON.parse(variantInCart);
        return variantInCartData;
      }

      compareVariantInCart(variantId) {
        const variantInCart = this.querySelector("variant-selects [data-variant-in-cart]")?.innerHTML;
        if (!variantInCart) {
          return;
        }

        const variantInCartData = JSON.parse(variantInCart);

        if (variantId === variantInCartData.variant_id && variantInCartData.variant_count_in_cart === variantInCartData.inventory_quantity && variantInCartData.inventory_policy !== "continue") {
          this.setInventoryLimit();
          return;
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector("variant-selects [data-selected-variant]")?.innerHTML;

        if (selectedVariant) {
          let selectedVariantData;
          try {
            selectedVariantData = JSON.parse(selectedVariant);
            if (selectedVariantData?.id && selectedVariantData?.title) {
              this.checkStockForVariant(selectedVariantData.id, selectedVariantData.title);
            } else {
              console.warn("Selected variant data is incomplete:", selectedVariantData);
            }
          } catch (error) {
            console.error("Error parsing selected variant JSON:", error);
            return null;
          }
          return selectedVariantData;
        }
        return null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          const variantInventory = variant.inventory_quantity;
          const variantPolicy = variant.inventory_policy;
          const variantInCart = this.getVariantInCart(html);
          const variantInCartCount = parseInt(variantInCart.variant_count_in_cart);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (variantInCartCount > 0 && variantInventory > 0 && variantInCartCount === variantInventory && variantPolicy !== "continue") {
            this.setInventoryLimit();
            return;
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.updateMediaGrouping();

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) => classList.contains("hidden"));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove("hidden");
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute("disabled") ?? true, window.variantStrings.soldOut);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`);

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", `${url}${variantId ? `?variant=${variantId}` : ""}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ["price", "Inventory", "Sku", "Price-Per-Item", "Volume-Note", "Volume", "Quantity-Rules"].map((id) => `#${id}-${this.dataset.section}`).join(", ");
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add("hidden"));
      }

      setInventoryLimit() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.inventory_limit_reached);

        const selectors = ["Inventory", "Sku", "Price-Per-Item", "Volume-Note", "Volume", "Quantity-Rules"].map((id) => `#${id}-${this.dataset.section}`).join(", ");
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll("li[data-media-id]"));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll("li[data-media-id]"));
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(sourceData.item, mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`));

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateMediaGrouping() {
        // Helper function: Convert string to 'handle' format
        const toHandle = (str) => {
          return str
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
            .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
        };

        // Select the main media gallery source
        const mediaGallerySource = this.querySelector("media-gallery ul");
        if (!mediaGallerySource) {
          console.error("Media gallery source not found.");
          return;
        }

        // Get all dot buttons and wrapper
        const dotButtons = Array.from(this.querySelectorAll(".slider-counter__link"));
        const dotsWrapper = this.querySelector(".slider-buttons"); // Parent of dots

        // Get all media items
        const mediaItems = Array.from(mediaGallerySource.querySelectorAll("li"));
        if (!mediaItems.length) {
          console.error("No media items found in the media gallery.");
          return;
        }

        // Select thumbnails if available
        const thumbnailSource = this.querySelector(".thumbnail-list");
        const thumbnailItems = thumbnailSource ? Array.from(thumbnailSource.querySelectorAll("li")) : [];

        // Get the selected variant
        const selectedVariant = this.getSelectedVariant(this);
        if (!selectedVariant) {
          console.warn("No variant selected.");
          return;
        }

        // Retrieve the featured media or fallback
        let featuredMedia = selectedVariant.featured_media;
        if (!featuredMedia) {
          console.warn("No featured media found, falling back to the first media item.");
          const firstMediaElement = mediaItems[0]?.querySelector(".media > *");
          if (firstMediaElement) {
            featuredMedia = {
              alt: firstMediaElement.getAttribute("alt") || "default",
            };
          }
        }

        // Determine selected color based on alt
        let selectedColor = featuredMedia?.alt ? toHandle(featuredMedia.alt) : null;

        // Filter media items and track visible ones
        const visibleMedia = [];
        mediaItems.forEach((mediaItem) => {
          const mediaElement = mediaItem.querySelector(".media > *");
          const mediaAltText = mediaElement?.getAttribute("alt")?.toLowerCase().trim();

          if (!mediaAltText || toHandle(mediaAltText) === selectedColor) {
            mediaItem.classList.remove("hidden");
            visibleMedia.push(mediaItem);
          } else {
            mediaItem.classList.add("hidden");
          }
        });

        // Process thumbnails
        thumbnailItems.forEach((thumbnail) => {
          const targetMediaId = thumbnail.getAttribute("data-target");
          if (visibleMedia.some((item) => item.getAttribute("data-media-id") === targetMediaId)) {
            thumbnail.classList.remove("hidden");
          } else {
            thumbnail.classList.add("hidden");
          }
        });

        // **Update Dot Buttons Based on Visible Slides**
        const visibleDotCount = visibleMedia.length;

        dotButtons.forEach((dot, index) => {
          // Show only dots that match the number of visible slides
          if (index < visibleDotCount) {
            dot.classList.remove("hidden");
          } else {
            dot.classList.add("hidden");
          }

          // Set active dot based on index
          if (index === 0) {
            dot.classList.add("slider-counter__link--active");
            dot.setAttribute("aria-current", "true");
          } else {
            dot.classList.remove("slider-counter__link--active");
            dot.removeAttribute("aria-current");
          }
        });

        // **Hide Slider Buttons If Only One Dot**
        if (dotsWrapper) {
          if (visibleDotCount <= 1) {
            dotsWrapper.classList.add("hidden"); // Hide parent wrapper
          } else {
            dotsWrapper.classList.remove("hidden"); // Show parent wrapper
          }
        }

        // Dispatch an event to indicate that the media grouping has been updated
        this.dispatchEvent(
          new CustomEvent("mediaGroupingUpdated", {
            bubbles: true,
            detail: { message: "Media grouping updated." },
          })
        );
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart .loading__spinner").classList.remove("hidden");
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector(".quantity__rules-cart .loading__spinner").classList.add("hidden"));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = [".quantity__input", ".quantity__rules", ".quantity__label"];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = ["data-cart-quantity", "data-min", "data-max", "step"];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
                return valueUpdated;
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), "related-products");
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), "quick_order_list");
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
